import {
  cookieValues,
  fbcCookie,
  pickFbc,
  shouldReplaceFbc,
} from '@/lib/fbc-cookie';
import { getQueryClient } from '@/lib/query-client';
import { TENANT_QUERY_KEY, type Tenant } from '@/state/tenant';
import { USER_QUERY_KEY, type User } from '@/state/user';
import {
  sendMetaConversion,
  type MetaCustomData,
  type MetaEventName,
  type MetaUserData,
  type MetaUserSource,
} from '@/services/tracking';

declare global {
  interface Window {
    fbq: (
      type: 'init' | 'track' | 'trackCustom' | 'set',
      eventOrPixelId: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string }
    ) => void;
    _fbq: unknown;
    oaiq: (
      command: 'init' | 'measure',
      eventOrConfig: string | Record<string, unknown>,
      props?: Record<string, unknown>,
      options?: { event_id?: string }
    ) => void;
    gtag: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      params?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

export function isValidGA4Id(id: string | null | undefined): id is string {
  return typeof id === 'string' && /^G-[A-Z0-9]+$/.test(id);
}

export function isValidGtmId(id: string | null | undefined): id is string {
  return typeof id === 'string' && /^GTM-[A-Z0-9]+$/.test(id);
}

export function isValidMetaPixelId(
  id: string | null | undefined
): id is string {
  return typeof id === 'string' && /^\d+$/.test(id);
}

export function isValidOpenAiPixelId(
  id: string | null | undefined
): id is string {
  return typeof id === 'string' && /^[A-Za-z0-9]{16,32}$/.test(id);
}

export function trackGAPageview(measurementId: string): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('config', measurementId);
  }
}

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const urlFbclid = (): string | null =>
  new URLSearchParams(window.location.search).get('fbclid');

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  if (name === '_fbc') return pickFbc(document.cookie, urlFbclid());
  return cookieValues(document.cookie, name)[0];
}

let metaSessionUser: MetaUserSource = {};

function mergeSource(
  base: MetaUserSource,
  patch: MetaUserSource
): MetaUserSource {
  const out: MetaUserSource = { ...base };
  (Object.keys(patch) as (keyof MetaUserSource)[]).forEach((k) => {
    const v = patch[k];
    if (v) out[k] = v;
  });
  return out;
}

function readCachedUser(): MetaUserSource {
  const u = getQueryClient().getQueryData<User>(USER_QUERY_KEY);
  const authed: MetaUserSource = u
    ? {
        email: u.email,
        phone: u.phone,
        first_name: u.first_name,
        last_name: u.last_name,
        country_code: u.country_code,
        user_id: u.user_id,
      }
    : {};
  return mergeSource(authed, metaSessionUser);
}

function normalize(field: keyof MetaUserSource, value: string): string {
  if (field === 'phone') return value.replace(/\D/g, '');
  if (field === 'user_id') return value;
  return value.trim().toLowerCase();
}

async function hashSourceField(
  field: keyof MetaUserSource,
  value: string
): Promise<string> {
  return sha256Hex(normalize(field, value));
}

async function buildUserData(
  source: MetaUserSource
): Promise<MetaUserData> {
  const data: MetaUserData = {};
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) data.fbp = fbp;
  if (fbc) data.fbc = fbc;
  if (source.email) data.em = [await hashSourceField('email', source.email)];
  if (source.phone) data.ph = [await hashSourceField('phone', source.phone)];
  if (source.first_name)
    data.fn = [await hashSourceField('first_name', source.first_name)];
  if (source.last_name)
    data.ln = [await hashSourceField('last_name', source.last_name)];
  if (source.country_code)
    data.country = [await hashSourceField('country_code', source.country_code)];
  if (source.user_id)
    data.external_id = [await hashSourceField('user_id', source.user_id)];
  return data;
}

export async function setMetaAdvancedMatching(
  source: MetaUserSource
): Promise<void> {
  metaSessionUser = mergeSource(metaSessionUser, source);
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  const merged = readCachedUser();
  const payload: Record<string, string> = {};
  if (merged.email)
    payload.em = await hashSourceField('email', merged.email);
  if (merged.phone)
    payload.ph = await hashSourceField('phone', merged.phone);
  if (merged.first_name)
    payload.fn = await hashSourceField('first_name', merged.first_name);
  if (merged.last_name)
    payload.ln = await hashSourceField('last_name', merged.last_name);
  if (merged.country_code)
    payload.country = await hashSourceField('country_code', merged.country_code);
  if (merged.user_id)
    payload.external_id = await hashSourceField('user_id', merged.user_id);
  if (Object.keys(payload).length === 0) return;
  window.fbq('set', 'userData', payload);
}

export function captureFbclidAsFbc(): void {
  if (typeof window === 'undefined') return;
  const fbclid = urlFbclid();
  if (!fbclid) return;
  if (!shouldReplaceFbc(readCookie('_fbc'), fbclid)) return;
  document.cookie = fbcCookie(fbclid, Date.now());
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// GA4 recommended ecommerce names, so the tenant's container gets Google's
// built-in triggers and Google Ads understands purchase natively. PageView is
// deliberately absent: SPA pageviews belong to the container's own History
// Change trigger, and mirroring one here would double-count. Schedule has no
// ecommerce equivalent and goes out as a custom event.
const GA4_EVENT_NAMES: Partial<Record<MetaEventName, string>> = {
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  InitiateCheckout: 'begin_checkout',
  Purchase: 'purchase',
  Schedule: 'schedule',
};

function readTenantGtmId(): string | null {
  const tenant = getQueryClient().getQueryData<Tenant>(TENANT_QUERY_KEY);
  return tenant?.tracking_config?.google_tag_manager_id ?? null;
}

// Gated on a valid container id so pixel-only tenants never grow an undrained
// dataLayer array. The null ecommerce push first stops a previous event's
// items bleeding into this one inside GTM's data model.
function pushGtmEvent(
  eventName: string,
  transactionId: string | undefined,
  custom_data: MetaCustomData | undefined
): void {
  if (typeof window === 'undefined') return;
  if (!isValidGtmId(readTenantGtmId())) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: eventName,
    ...(custom_data && {
      ecommerce: {
        ...(transactionId && { transaction_id: transactionId }),
        ...(custom_data.value !== undefined && { value: custom_data.value }),
        ...(custom_data.currency && { currency: custom_data.currency }),
        items: [
          {
            ...(custom_data.content_ids?.[0] && {
              item_id: custom_data.content_ids[0],
            }),
            ...(custom_data.content_name && {
              item_name: custom_data.content_name,
            }),
            ...(custom_data.content_type && {
              item_category: custom_data.content_type,
            }),
            quantity: custom_data.num_items ?? 1,
          },
        ],
      },
    }),
  });
}

// OpenAI standard event names with the type each expects. Unknown names are
// dropped by OpenAI reporting, so only this fixed vocabulary goes out.
// Purchase maps by product below: a subscription enrolls a plan, everything
// else is an order.
const OAIQ_EVENTS: Partial<
  Record<MetaEventName, { name: string; type: string }>
> = {
  PageView: { name: 'page_viewed', type: 'contents' },
  ViewContent: { name: 'contents_viewed', type: 'contents' },
  AddToCart: { name: 'items_added', type: 'contents' },
  InitiateCheckout: { name: 'checkout_started', type: 'contents' },
  Purchase: { name: 'order_created', type: 'contents' },
  Schedule: { name: 'appointment_scheduled', type: 'customer_action' },
};

function readTenantOpenAiPixelId(): string | null {
  const tenant = getQueryClient().getQueryData<Tenant>(TENANT_QUERY_KEY);
  return tenant?.tracking_config?.openai_pixel_id ?? null;
}

// OpenAI takes integer minor units (2599 means 25.99) while Meta takes decimal
// major units, hence the conversion. The shared eventId is what the backend's
// OpenAI CAPI mirror dedupes on, the same way the Meta one does.
function pushOaiqEvent(
  event: MetaEventName,
  custom_data: MetaCustomData | undefined,
  eventId: string
): void {
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') return;
  if (!isValidOpenAiPixelId(readTenantOpenAiPixelId())) return;

  const mapped = OAIQ_EVENTS[event];
  if (!mapped) return;

  const isSubscription =
    event === 'Purchase' && custom_data?.content_type === 'subscription';

  window.oaiq(
    'measure',
    isSubscription ? 'subscription_created' : mapped.name,
    {
      type: isSubscription ? 'plan_enrollment' : mapped.type,
      ...(custom_data?.value !== undefined && {
        amount: Math.round(custom_data.value * 100),
        ...(custom_data.currency && { currency: custom_data.currency }),
      }),
      ...(custom_data?.content_ids?.length && {
        contents: custom_data.content_ids.map((id) => ({
          id,
          ...(custom_data.content_name && { name: custom_data.content_name }),
          content_type: 'product',
          quantity: custom_data.num_items ?? 1,
        })),
      }),
    },
    { event_id: eventId }
  );
}

export type TrackMetaConversionInput = {
  event: MetaEventName;
  custom_data?: MetaCustomData;
  event_id?: string;
  // GA4 purchase key that lets Google Ads dedupe a conversion counted twice.
  // Pass the order id, the same one already inside the Purchase event_id;
  // passed explicitly because Schedule shares that id under its own prefix.
  transaction_id?: string;
  skipCapi?: boolean;
};

// Meta discards a value with no currency, and OpenAI's amount field is
// meaningless without one, so an absent code has to be dropped rather than sent
// as null. Done once here instead of at each call site: this fans out to four
// destinations, and ten callers each remembering their own guard is how three
// of them ended up guarded and seven not.
const withoutNullCurrency = (
  custom_data: MetaCustomData | undefined
): MetaCustomData | undefined => {
  if (!custom_data || custom_data.currency) return custom_data;
  const { currency: _currency, ...rest } = custom_data;
  return rest;
};

export function trackMetaConversion({
  event,
  custom_data: rawCustomData,
  event_id,
  transaction_id,
  skipCapi,
}: TrackMetaConversionInput): void {
  const custom_data = withoutNullCurrency(rawCustomData);
  const eventId = event_id ?? generateEventId();
  const eventTime = Math.floor(Date.now() / 1000);
  const sourceUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', event, custom_data ?? {}, { eventID: eventId });
  }

  const ga4Event = GA4_EVENT_NAMES[event];
  if (ga4Event) pushGtmEvent(ga4Event, transaction_id, custom_data);

  pushOaiqEvent(event, custom_data, eventId);

  if (skipCapi) return;

  buildUserData(readCachedUser()).then((user_data) => {
    sendMetaConversion({
      event_name: event,
      event_id: eventId,
      event_time: eventTime,
      event_source_url: sourceUrl,
      user_data,
      custom_data,
    });
  });
}
